// frontend/src/components/MedicationRow.js
//
// Extracted from PrescriptionTab so the same medication editor can be
// rendered from Objective (quick-prescribe while examining) and from
// Plan > Prescription/Medication tab, both backed by the same
// PrescriptionContext state.

import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { SearchIcon } from "lucide-react";
import { httpService } from "../../services/httpService";
import { computeFixedDropdownPosition, fixedDropdownStyle } from "./dropdownPositionUtils";

// ─── Micro-components ──────────────────────────────────────────────────────

export const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
    {children}
  </p>
);

export const SelectField = ({ value, onChange, options, placeholder, className = "", inputRef }) => (
  <div className={`relative ${className}`}>
    <select
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 appearance-none text-[13px] text-slate-700 border border-slate-200 rounded-lg px-3 pr-8 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent bg-white"
    >
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
    <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
  </div>
);

// ─── Searchable Medication Dropdown ────────────────────────────────────────

const SearchableMedicationSelect = ({ value, onSelect, onCancel, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, flipUp: false });
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchMedications = async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setMedications([]);
        return;
      }
      setLoading(true);
      try {
        const response = await httpService.get(
          `/vendorproduct/searchvendorproducts?search=${encodeURIComponent(searchTerm)}`
        );
        if (response.code === 200) {
          const mapped = (response.data || []).map((p) => ({
            _id: p._id,
            name: p.name,
            sku_number: p.sku_number || "",
            unit: p.unit || "",
            sale_price: p.sale_price || 0,
            mrp: p.mrp || 0,
            gst_slab: p.gst_slab || 0,
            brand: p.product_brand_id?.name || "",
            product_brand_id: p.product_brand_id,
          }));
          setMedications(mapped);
          setHighlightedIndex(0);
        } else {
          setMedications([]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        setMedications([]);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      if (isOpen) fetchMedications();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
        onCancel?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onCancel]);

  const computeDropdownPosition = () => {
    if (!triggerRef.current) return;
    setDropdownPosition(computeFixedDropdownPosition(triggerRef.current, { maxHeight: 256, gap: 4 }));
  };

  useEffect(() => {
    if (isOpen) computeDropdownPosition();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => computeDropdownPosition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen]);

  const openDropdown = () => setIsOpen(true);

  const handleSelect = (med) => {
    onSelect(med);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setSearchTerm("");
    onCancel?.();
  };

  const handleTriggerKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      openDropdown();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(medications.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (medications[highlightedIndex]) handleSelect(medications[highlightedIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-slate-200 rounded-lg shadow-lg z-[9999] max-h-56 overflow-y-auto"
      style={fixedDropdownStyle(dropdownPosition)}
    >
      <div className="sticky top-0 bg-white p-2 border-b border-slate-100">
        <div className="relative">
          <SearchIcon className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Type to search... (↑↓ to navigate, Enter to pick)"
            className="w-full pl-7 pr-2 py-1.5 text-[12px] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300"
            autoFocus
          />
        </div>
      </div>
      {!searchTerm || searchTerm.length < 2 ? (
        <div className="px-3 py-2 text-[11px] text-slate-400 text-center">Type at least 2 characters…</div>
      ) : loading ? (
        <div className="px-3 py-2 text-[11px] text-slate-400 text-center">Loading...</div>
      ) : medications.length > 0 ? (
        medications.map((med, idx) => (
          <button
            key={med._id || med.name}
            type="button"
            onClick={() => handleSelect(med)}
            onMouseEnter={() => setHighlightedIndex(idx)}
            className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${
              idx === highlightedIndex ? "bg-emerald-50" : "hover:bg-emerald-50"
            }`}
          >
            <span className="text-[12px] font-medium text-slate-700 truncate">{med.name}</span>
            <span className="text-[10px] text-slate-400 shrink-0 ml-2">{med.unit || ""}</span>
          </button>
        ))
      ) : (
        <div className="px-3 py-2 text-[11px] text-slate-400 text-center">
          No medications found
        </div>
      )}
    </div>
  );

  return (
    <div className="relative w-full">
      <button
        type="button"
        ref={triggerRef}
        onClick={() => (isOpen ? handleCancel() : openDropdown())}
        onKeyDown={handleTriggerKeyDown}
        className="w-full h-9 flex items-center justify-between px-3 border border-slate-200 rounded-lg bg-white cursor-pointer hover:border-emerald-300 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300"
      >
        <span className={`text-[12px] truncate ${value ? "text-slate-800 font-medium" : "text-slate-400"}`}>
          {value?.name || value || placeholder || "Search medication..."}
        </span>
        <ChevronDownIcon className={`w-3 h-3 text-slate-400 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

// ─── Dosage schedule config ────────────────────────────────────────────────

export const TIME_SLOTS = [
  { key: "morning", label: "Morning", short: "Morning" },
  { key: "afternoon", label: "Afternoon", short: "Afternoon" },
  { key: "night", label: "Night", short: "Night" },
];

export const FOOD_TIMING_OPTIONS = ["Before Food", "After Food", "With Food", "Empty Stomach", "Any Time"];

const DOSE_STEP = 0.5;
const DOSE_MAX = 5;

export const formatDoseQty = (n) => {
  const num = parseFloat(n) || 0;
  if (num <= 0) return "0";
  const whole = Math.floor(num);
  const frac = +(num - whole).toFixed(2);
  if (frac === 0) return `${whole}`;
  if (frac === 0.5) {
    // Use a non-breaking space or em-space for better readability
    return whole > 0 ? `${whole}\u00A0½` : "½";
  }
  return `${num}`;
};

export const buildDosageSummary = (item) => {
  const parts = TIME_SLOTS
    .filter((slot) => (parseFloat(item[slot.key]) || 0) > 0)
    .map((slot) => {
      const dose = formatDoseQty(item[slot.key]);
      // For display in summary, use a more compact format
      return `${dose} ${slot.label}`;
    });
  return parts.join(", ");
};

export const calcTotalQty = (item) => {
  const perDose = TIME_SLOTS.reduce((sum, slot) => sum + (parseFloat(item[slot.key]) || 0), 0);
  if (perDose <= 0) return null;
  const days = parseFloat(item.duration) || 0;
  if (days <= 0) return null;
  return +(perDose * days).toFixed(2);
};

// ─── Dose Qty Control ───────────────────────────────────────────────────────

const DoseQtyControl = ({ value, onChange, inputRef }) => {
  const committed = value > 0 ? value : DOSE_STEP * 2;
  const [text, setText] = useState(formatDoseQty(committed));

  useEffect(() => {
    setText(formatDoseQty(committed));
  }, [committed]);

  // Helper to parse fractional notation like "1 1/2", "1/2", "11/2", etc.
  const parseFractionalInput = (raw) => {
    const trimmed = raw.trim();
    
    // Check if it contains a space (e.g., "1 1/2")
    if (trimmed.includes(' ')) {
      const parts = trimmed.split(' ');
      const whole = parseInt(parts[0]) || 0;
      const frac = parts[1] || '';
      if (frac.includes('/')) {
        const [num, den] = frac.split('/').map(Number);
        if (den > 0) {
          return whole + (num / den);
        }
      }
      return whole;
    }
    
    // Check if it's a fraction (e.g., "1/2", "3/4")
    if (trimmed.includes('/')) {
      const [num, den] = trimmed.split('/').map(Number);
      if (den > 0) {
        return num / den;
      }
    }
    
    // Try parsing as decimal
    const parsed = parseFloat(trimmed);
    return isNaN(parsed) ? null : parsed;
  };

  const commit = (raw) => {
    let parsed = parseFractionalInput(raw);
    
    if (parsed === null || parsed <= 0) {
      parsed = committed;
    } else {
      parsed = Math.min(DOSE_MAX, Math.round(parsed * 2) / 2);
    }
    
    onChange(parsed);
    setText(formatDoseQty(parsed));
  };

  const bump = (delta) => {
    const next = Math.min(DOSE_MAX, Math.max(DOSE_STEP, Math.round((committed + delta) * 2) / 2));
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Dose</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          tabIndex={-1}
          onClick={() => bump(-DOSE_STEP)}
          className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-emerald-600 border border-slate-200 rounded-lg hover:border-emerald-300 transition-colors text-[16px] leading-none"
          aria-label="Decrease dose"
        >
          −
        </button>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commit(e.currentTarget.value);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              bump(DOSE_STEP);
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              bump(-DOSE_STEP);
            }
          }}
          className="w-20 h-9 text-[13px] font-semibold text-slate-800 text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300 font-mono tracking-wide"
          style={{ 
            fontSize: text.length > 4 ? '11px' : '14px',
            letterSpacing: text.includes(' ') ? '0.5px' : 'normal'
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => bump(DOSE_STEP)}
          className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-emerald-600 border border-slate-200 rounded-lg hover:border-emerald-300 transition-colors text-[16px] leading-none"
          aria-label="Increase dose"
        >
          +
        </button>
      </div>
    </div>
  );
};

// ─── Time Slot Toggle ───────────────────────────────────────────────────────

const TimeSlotToggles = ({ item, onToggle }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Take at</span>
    <div className="flex items-center gap-2">
      {TIME_SLOTS.map((slot) => {
        const active = (parseFloat(item[slot.key]) || 0) > 0;
        return (
          <button
            key={slot.key}
            type="button"
            onClick={() => onToggle(slot.key)}
            className={`h-9 px-4 text-[13px] font-bold rounded-lg border transition-colors ${
              active
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "border-slate-200 text-slate-400 hover:border-slate-300"
            }`}
          >
            {slot.short}
          </button>
        );
      })}
    </div>
  </div>
);

// ─── Medication Row ─────────────────────────────────────────────────────────

export const MedicationRow = ({ item, onChange, onRemove }) => {
  const [isEditing, setIsEditing] = useState(!item.drugName);

  const handleMedSelect = (med) => {
    onChange({
      ...item,
      selectedMed: med,
      drugName: med.name,
      product_id: med._id,
      unit: med.unit || "",
      sku_number: med.sku_number || "",
      sale_price: med.sale_price || 0,
      mrp: med.mrp || 0,
      gst_slab: med.gst_slab || 0,
      brand: med.brand || "",
      product_brand_id: med.product_brand_id || null,
    });
    setIsEditing(false);
  };

  const handleCancelEdit = () => setIsEditing(false);

  const getSharedDose = () => {
    for (const slot of TIME_SLOTS) {
      const v = parseFloat(item[slot.key]) || 0;
      if (v > 0) return v;
    }
    return DOSE_STEP * 2;
  };

  const handleDoseQtyChange = (newQty) => {
    const updated = { ...item };
    TIME_SLOTS.forEach((slot) => {
      if ((parseFloat(item[slot.key]) || 0) > 0) {
        updated[slot.key] = newQty;
      }
    });
    onChange(updated);
  };

  const handleToggleSlot = (slotKey) => {
    const isActive = (parseFloat(item[slotKey]) || 0) > 0;
    onChange({ ...item, [slotKey]: isActive ? 0 : getSharedDose() });
  };

  const dosageSummary = buildDosageSummary(item);
  const totalQty = calcTotalQty(item);
  const hasDosage = TIME_SLOTS.some((slot) => (parseFloat(item[slot.key]) || 0) > 0);

  const handleDurationChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      onChange({ ...item, duration: value === "" ? "" : Number(value) });
    }
  };

  const handleDurationBlur = (e) => {
    const value = e.target.value;
    if (value === "" || isNaN(Number(value)) || Number(value) < 1) {
      onChange({ ...item, duration: 1 });
    }
  };

  return (
    <div className="px-5 py-4 bg-white border border-slate-100 rounded-lg space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-6 flex-1 min-w-0">
          <div className="flex-1 min-w-[200px] max-w-[400px]">
            {!isEditing && item.drugName ? (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Medication</span>
                <div className="flex items-center gap-2.5">
                  <span className="text-[15px] font-semibold text-slate-800 truncate">{item.drugName}</span>
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="text-[12px] text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap"
                  >
                    Change
                  </button>
                </div>
                {/* Brand and SKU on the same line */}
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  {item.brand && (
                    <span>Brand: {item.brand}</span>
                  )}
                  {item.sku_number && (
                    <span>SKU: {item.sku_number}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Medication</span>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <SearchableMedicationSelect
                      value={item.selectedMed}
                      onSelect={handleMedSelect}
                      onCancel={handleCancelEdit}
                      placeholder="Search medication..."
                    />
                  </div>
                  {item.drugName && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="text-[12px] text-slate-400 hover:text-slate-600 font-medium whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0">
            <TimeSlotToggles item={item} onToggle={handleToggleSlot} />
          </div>
        </div>

        {hasDosage && (
          <div className="text-[14px] text-slate-600 text-right shrink-0 max-w-[280px]">
            <div className="font-semibold text-emerald-700 tracking-wide">
              {dosageSummary}
            </div>
            {item.foodTiming && (
              <div className="text-[11px] text-slate-400 mt-0.5">{item.foodTiming}</div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <DoseQtyControl value={getSharedDose()} onChange={handleDoseQtyChange} />
        </div>

        <div className="flex flex-col gap-1 min-w-[100px]">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Unit</span>
          <div className="w-full h-9 flex items-center px-3 border border-slate-200 rounded-lg bg-slate-50 text-[13px] text-slate-600 cursor-not-allowed">
            {item.unit || "—"}
          </div>
        </div>

        <div className="flex flex-col gap-1 min-w-[140px] flex-1 max-w-[200px]">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Food Timing</span>
          <SelectField
            value={item.foodTiming || ""}
            onChange={(v) => onChange({ ...item, foodTiming: v })}
            options={FOOD_TIMING_OPTIONS}
            placeholder="Select timing"
            className="w-full"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Duration</span>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={item.duration ?? 1}
              onChange={handleDurationChange}
              onBlur={handleDurationBlur}
              placeholder="Days"
              className="w-16 h-9 text-[14px] text-slate-700 text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />
            <span className="text-[13px] text-slate-500 font-medium">Days</span>
          </div>
        </div>

        {totalQty !== null && item.unit && hasDosage && (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total</span>
            <div className="text-[15px] font-bold text-emerald-600 whitespace-nowrap">
              {totalQty % 1 === 0 ? totalQty : totalQty.toFixed(1)} {item.unit}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove medication"
          className="h-9 w-9 flex items-center justify-center text-slate-400 border border-slate-200 rounded-lg hover:text-red-500 hover:border-red-300 hover:bg-red-50 transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          Note (optional)
        </span>
        <input
          type="text"
          value={item.note || ""}
          onChange={(e) => onChange({ ...item, note: e.target.value })}
          placeholder="e.g. Give with warm water, avoid dairy..."
          className="w-full h-9 text-[13px] text-slate-700 border border-slate-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
        />
      </div>
    </div>
  );
};

export const makeMedId = () => `med-${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const emptyMedication = () => ({
  id: makeMedId(),
  drugName: "",
  selectedMed: null,
  product_id: null,
  morning: "",
  afternoon: "",
  night: "",
  foodTiming: "",
  duration: "1",
  unit: "",
  note: "",
  sku_number: "",
  sale_price: 0,
  mrp: 0,
  gst_slab: 0,
  brand: "",
  product_brand_id: null,
});